import React, { useEffect } from "react";
import Header from "../../components/Header";
import { Box, Button, IconButton, Tab, Tabs } from "@mui/material";
import BackButton from "../../components/BackButton";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";
import { useState } from "react";
import Tier1Table from "../../components/tiersTable/Tier1Table";
import Tier2Table from "../../components/tiersTable/Tier2Table";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import Tier3Table from "../../components/tiersTable/Tier3Table";
import Tier4Table from "../../components/tiersTable/Tier4Table";
import CreateCacAccountModal from "../../components/modals/CreateCacAccountModal";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserKYC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [value, setValue] = useState(0);
  const [tierDetails, setTierDetails] = useState([]);
  const axiosPrivate = useAxiosPrivate();
  const { queryUserDetails, setQueryUserDetails, success, snackError } = useAuth();
  const userId = queryUserDetails?.manageKYC?.id || queryUserDetails?.manageKYC?.userId || queryUserDetails?.user[0]?.id || queryUserDetails?.user[0]?.userId || "";
  const [bvn, setBvn] = useState("");
  const [nin, setNin] = useState("");
  const [ninImage, setNinImage] = useState("");
  const [ninDetails, setNinDetails] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [cacModalOpen, setCacModalOpen] = useState(false);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleNext = () => {
    setValue((prevValue) => (prevValue + 1) % 4);
  };

  const handlePrev = () => {
    setValue((prevValue) => (prevValue + 3) % 4);
  };

  const handleForceRestoreKyc = async () => {
    if (!window.confirm("Are you sure you want to force restore KYC for this user?")) {
      return;
    }
    setIsRestoring(true);
    try {
      const response = await axiosPrivate.post(
        `/auth-serviceV3/api/v1/admin/users/${userId}/tier-restoration/force`,
        {}
      );
      success(response?.data?.message || "KYC restored successfully!");
    } catch (error) {
      console.log(error);
      snackError(error?.response?.data?.message || "Failed to restore KYC.");
    } finally {
      setIsRestoring(false);
    }
  };

  useEffect(() => {
    async function getKycRequirementsData() {
      try {
        const response = await axiosPrivate.post(
          "/kyc-servicev2/api/v1/admin/kyc/requirementsData/" + userId
        );
        setTierDetails(response?.data?.data);
      } catch (error) {
        console.log(error);
      }
    }

    async function getUserProfile() {
      try {
        const response = await axiosPrivate.get(
          "/auth-serviceV3/admin/users/" + userId
        );
        const profileData = response?.data?.data;
        if (profileData) {
          setBvn(profileData.bvn || "");
          setQueryUserDetails((prev) => ({
            ...prev,
            manageKYC: {
              ...prev.manageKYC,
              ...profileData,
            },
          }));
        }
      } catch (error) {
        console.log(error);
      }
    }

    async function getNinVerification() {
      try {
        const response = await axiosPrivate.get(
          "/auth-serviceV3/nin-verification/user/" + userId
        );
        const ninData = response?.data?.data;
        if (Array.isArray(ninData) && ninData.length > 0) {
          setNin(ninData[0].nin || "");
          setNinImage(ninData[0].imageUrl || "");
          setNinDetails(ninData[0]);
        }
      } catch (error) {
        console.log(error);
      }
    }

    if (userId) {
      getKycRequirementsData();
      getUserProfile();
      getNinVerification();
    }
  }, [userId]);

  return (
    <Box m="20px">
      <BackButton path="/" />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Update User KYC" subtitle="Manage User KYC requirements" />
        <Box display="flex" gap="10px" sx={{ mb: "30px" }}>
          <Button
            variant="contained"
            onClick={() => setCacModalOpen(true)}
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
          >
            Create CAC Account
          </Button>
          <Button
            variant="contained"
            onClick={handleForceRestoreKyc}
            disabled={isRestoring}
            sx={{
              backgroundColor: colors.orangeAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
          >
            {isRestoring ? "Restoring..." : "Force Restore KYC"}
          </Button>
        </Box>
      </Box>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IconButton onClick={handlePrev}>
              <ArrowBackIos />
            </IconButton>
            <Tabs
              value={value}
              onChange={handleChange}
              sx={{
                bgcolor: colors.primary[400],
              }}
              aria-label="My Tabs"
            >
              <Tab
                sx={{
                  borderRadius: "5px",
                  bgcolor: colors.primary[400],
                  "&.Mui-selected": {
                    color: colors.grey[100],
                    bgcolor: colors.orangeAccent[700],
                  },
                }}
                label="Tier 1"
                id="tier-0"
                aria-controls="tabpanel-0"
              />
              <Tab
                sx={{
                  borderRadius: "5px",
                  bgcolor: colors.primary[400],
                  "&.Mui-selected": {
                    color: colors.grey[100],
                    bgcolor: colors.orangeAccent[700],
                  },
                }}
                label="Tier 2"
                id="tier-1"
                aria-controls="tabpanel-1"
              />
              <Tab
                sx={{
                  borderRadius: "5px",
                  bgcolor: colors.primary[400],
                  "&.Mui-selected": {
                    color: colors.grey[100],
                    bgcolor: colors.orangeAccent[700],
                  },
                }}
                label="Tier 3"
                id="tier-2"
                aria-controls="tabpanel-2"
              />
              <Tab
                sx={{
                  borderRadius: "5px",
                  bgcolor: colors.primary[400],
                  "&.Mui-selected": {
                    color: colors.grey[100],
                    bgcolor: colors.orangeAccent[700],
                  },
                }}
                label="Tier 4"
                id="tier-3"
                aria-controls="tabpanel-3"
              />
            </Tabs>
            <IconButton onClick={handleNext}>
              <ArrowForwardIos />
            </IconButton>
          </Box>
          <Box
            p={3}
            m={2}
            borderRadius={2}
            bgcolor={colors.primary[400]}
            sx={{ width: "90%" }}
          >
            <TabPanel value={value} index={0}>
              <Tier1Table />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <Tier2Table tier2Details={tierDetails?.tier2} bvn={bvn} />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <Tier3Table tier3Details={tierDetails?.tier3} nin={nin} ninImage={ninImage} ninDetails={ninDetails} />
            </TabPanel>
            <TabPanel value={value} index={3}>
              <Tier4Table tier4Details={tierDetails?.tier4} />
            </TabPanel>
          </Box>
        </Box>
      </Box>
      <CreateCacAccountModal
        isOpen={cacModalOpen}
        setModal={setCacModalOpen}
        userId={userId}
        userEmail={queryUserDetails?.manageKYC?.email || ""}
      />
    </Box>
  );
};

export default UserKYC;
